import React, { useState, useEffect, useCallback } from "react";
import SidebarTemplate from "../components/shared/SidebarTemplate";
import TranscriptViewer from "../components/TranscriptViewer";
import NotesManager from "../components/shared/NotesManager";
import {
  DEMO_TRANSCRIPT,
  DEMO_NOTES_SUMMARY,
  DEMO_NOTES_UPDATES,
  DEMO_NOTES_TASKS,
  MODAL_TYPES,
  normalizeTimeString,
  DEMO_TIMING,
} from "../demo/annualReviewDemoLogic.js";
import { useNavigate } from 'react-router-dom';
import { useDemo } from "../contexts/DemoContext";

// Minimum delay between messages even if timestamps are closer
const MIN_DELAY_MS = 500;
// Maximum delay if timestamps suggest a longer pause
const MAX_DELAY_MS = 1000;

const CAPTURED_DATA = {
  callSummary: {
    title: "✅ Call Summary",
    value: "Annual Financial Review with Thomas & Mary",
  },
  callDetails: {
    title: "Call Details",
    value: "Outgoing | Start: 8:00:37 AM | End: ~8:35 AM",
  },
  participants: {
    title: "Participants",
    value: "Advisor (Lead), Thomas (Client), Mary (Spouse, Retired)",
  },
  personalUpdates: {
    title: "📰 Personal & Life Updates",
    value:
      "Promotion with RSU grant, exploring early retirement at 64, recent Switzerland travel, good health status",
  },
  incomeCashflow: {
    title: "💵 Income & Cash Flow",
    value:
      "2025 Income: $141,787.52 | Monthly Net Gain: +$2,300 | Strong cash reserves",
  },
  balanceSheet: {
    title: "🏦 Balance Sheet",
    value:
      "Mortgage only liability, strong asset growth, 2,500 RSUs (2026-2030 vesting)",
  },
  riskTolerance: {
    title: "⚖️ Risk Assessment",
    value:
      "Conservative-moderate profile, reassessment pending via post-call questionnaire",
  },
  retirementModel: {
    title: "📈 Retirement Model",
    value:
      "Early retirement (64) feasible - Score 91/94, 95% confidence in sustainability",
  },
  investments: {
    title: "💼 Investment Strategy",
    value: "New PE funds proposed, pending risk assessment results",
  },
  taxStrategy: {
    title: "🧾 Tax Planning",
    value:
      "$40K Roth conversion proposed with 10% buffer, CPA confirmation needed",
  },
  estatePlanning: {
    title: "📜 Estate Planning",
    value: "On hold until retirement, streamlined service available",
  },
  followUp: {
    title: "📅 Next Steps",
    value:
      "3-week follow-up, comprehensive recap email with modeling and materials pending",
  },
};

export default function AnnualReviewDemo() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("transcript");
  const [notesHtml, setNotesHtml] = useState(
    Object.entries(CAPTURED_DATA)
      .map(
        ([key, field]) =>
          `<div class="field-group-styled">
        <div class="field-label-styled">${field.title}</div>
        <div class="field-value-styled">${field.value}</div>
      </div>`,
      )
      .join("\n"),
  );

  const navigate = useNavigate();
  const householdId = "253"; // Demo household ID from the demo data

  // Get demo state from context
  const {
    transcript,
    isRecording,
    isPaused,
    addTranscriptLine,
    onAction,
    onEffectComplete,
    currentIndex: contextIndex, // Get currentIndex from context
    setCurrentIndex: setContextIndex, // Get setCurrentIndex from context
    activeModal,
  } = useDemo();

  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);

  // Effect to handle transcript playback
  useEffect(() => {
    if (!isRecording || isPaused) {
      console.log('⏸️ Playback paused or not recording:', { 
        isRecording, 
        isPaused,
        contextIndex,
        transcriptLength: transcript?.length,
        demoTranscriptLength: DEMO_TRANSCRIPT.length
      });
      return;
    }

    // Safety check for transcript entry existence
    const currentEntry = DEMO_TRANSCRIPT[contextIndex];
    if (!currentEntry) {
      console.error('❌ No transcript entry at index:', contextIndex);
      return;
    }

    // Safety check to prevent re-firing the same modal
    if (currentEntry?.action?.type && currentEntry.action.type === activeModal?.type) {
      console.warn('⚠️ Already showing modal for this entry, skipping...', {
        actionType: currentEntry.action.type,
        currentIndex: contextIndex
      });
      return;
    }

    console.log('▶️ Playing transcript entry:', {
      index: contextIndex,
      entry: currentEntry,
      nextAction: currentEntry.action?.type,
      hasModal: !!activeModal
    });

    // Add the line to transcript
    addTranscriptLine(currentEntry);
    
    // Handle action if exists
    if (currentEntry.action) {
      console.log('🎯 Dispatching transcript action:', {
        type: currentEntry.action.type,
        index: contextIndex
      });
      onAction(currentEntry.action);
      return;
    }

    // Calculate delay until next line
    const nextEntry = DEMO_TRANSCRIPT[contextIndex + 1];
    let delay = MIN_DELAY_MS;
    if (nextEntry) {
      const currentTime = normalizeTimeString(currentEntry.timestamp);
      const nextTime = normalizeTimeString(nextEntry.timestamp);
      delay = Math.min(MAX_DELAY_MS, Math.max(MIN_DELAY_MS, (nextTime - currentTime) * 1000));
    }

    console.log('📝 Scheduling next line with delay:', delay, 'ms');

    // Schedule next line
    const timer = setTimeout(() => {
      console.log('📝 Adding transcript line:', {
        entry: currentEntry,
        contextIndex,
        transcriptLength: transcript?.length
      });
      
      // Increment index after confirming line was added
      setContextIndex(i => {
        console.log('🔄 Incrementing index:', i, '->', i + 1);
        return i + 1;
      });
      
      // Auto-open transcript on first line
      if (contextIndex === 0) {
        setIsTranscriptOpen(true);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [isRecording, isPaused, contextIndex, addTranscriptLine, onAction, setContextIndex, transcript?.length, activeModal]);

  // Add effect to monitor transcript growth
  useEffect(() => {
    console.log('🧮 Transcript stats:', {
      length: transcript?.length,
      contextIndex,
      nextEntry: DEMO_TRANSCRIPT[contextIndex],
      hasModal: !!activeModal
    });
  }, [transcript, contextIndex, activeModal]);

  // Create transcriptSlice based on currentIndex
  const transcriptSlice = React.useMemo(() => {
    console.log('🧾 Creating transcript slice:', {
      contextIndex,
      fullLength: transcript?.length,
      sliceLength: transcript?.slice(0, contextIndex + 1)?.length
    });
    return transcript?.slice(0, contextIndex + 1);
  }, [transcript, contextIndex]);

  // Modal action handler
  const handleModalAction = useCallback((type, data) => {
    switch (type) {
      case MODAL_TYPES.SMART_NAVIGATION:
        // Handle navigation
        break;
      case MODAL_TYPES.TASK_CREATION:
        // Handle task creation
        break;
      default:
        break;
    }
    onEffectComplete(); // Let context know we're done with this effect
  }, [onEffectComplete]);

  // Playback control handlers
  const handleStart = useCallback(() => {
    onAction({ type: 'START_DEMO' });
  }, [onAction]);

  const handlePause = useCallback(() => {
    onAction({ type: 'PAUSE' });
  }, [onAction]);

  const handleResume = useCallback(() => {
    onAction({ type: 'RESUME_DEMO' });
  }, [onAction]);

  const handleReset = useCallback(() => {
    onAction({ type: 'RESET_DEMO', preserveTranscript: true });
  }, [onAction]);

  return (
    <SidebarTemplate
      isExpanded={isSidebarOpen}
      onToggle={() => setIsSidebarOpen((open) => !open)}
      transcript={transcriptSlice}
      isRecording={isRecording}
      isPaused={isPaused}
      onModalAction={handleModalAction}
      key={`sidebar-${contextIndex}`} // Force re-render on index change
    >
      {activeTab === "transcript" ? (
        <div>
          {/* Recording controls */}
          <div style={{ marginBottom: 16, display: "flex", gap: 8 }}>
            {!isRecording ? (
              <button onClick={handleStart} className="start-button">
                Start Demo
              </button>
            ) : (
              <>
                <button
                  onClick={isPaused ? handleResume : handlePause}
                  className="pause-resume-button"
                >
                  {isPaused ? "Resume" : "Pause"}
                </button>
                <button onClick={handleReset} className="reset-button">
                  Reset
                </button>
              </>
            )}
          </div>
          <TranscriptViewer data={transcriptSlice} />
        </div>
      ) : (
        <NotesManager
          key="notes-manager"
          summary={notesHtml}
          onSave={setNotesHtml}
          capturedData={CAPTURED_DATA}
        />
      )}
    </SidebarTemplate>
  );
}
